import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  Select,
  MenuItem,
  LinearProgress,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  GpsFixed as TargetIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Store as StoreIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import apiService from '../services/api';
import { getCurrentMonthYearBrasilia } from '../utils/dateUtils';

interface SellerPerformance {
  id: string;
  name: string;
  monthlyGoal: number;
  goalIsFallback?: boolean;
  salesRealized: number;
  numberOfOrders: number;
  performance: number;
  bonus?: number;
}

interface BonusTier {
  minSales: number;
  maxSales: number | null;
  rate: number;
  fixedBonus: number;
}

interface DesignerTier {
  artsPerDay: number;
  monthlyArts: number;
  bonus: number;
}

const DESIGNER_TIERS: DesignerTier[] = [
  { artsPerDay: 24,  monthlyArts: 720,  bonus: 100  },
  { artsPerDay: 40,  monthlyArts: 1200, bonus: 150  },
  { artsPerDay: 50,  monthlyArts: 1500, bonus: 300  },
  { artsPerDay: 60,  monthlyArts: 1800, bonus: 350  },
  { artsPerDay: 70,  monthlyArts: 2100, bonus: 400  },
  { artsPerDay: 80,  monthlyArts: 2400, bonus: 500  },
  { artsPerDay: 90,  monthlyArts: 2700, bonus: 600  },
  { artsPerDay: 100, monthlyArts: 3000, bonus: 1000 },
];

interface DesignerPerformance {
  id: string;
  name: string;
  totalArts: number;
}

function getDesignerTierInfo(totalArts: number) {
  let currentTierIdx = -1;
  for (let i = 0; i < DESIGNER_TIERS.length; i++) {
    if (totalArts >= DESIGNER_TIERS[i].monthlyArts) currentTierIdx = i;
  }
  const currentTier = currentTierIdx >= 0 ? DESIGNER_TIERS[currentTierIdx] : null;
  const nextTier = currentTierIdx < DESIGNER_TIERS.length - 1 ? DESIGNER_TIERS[currentTierIdx + 1] : null;
  const bonus = currentTier?.bonus ?? 0;
  const targetTier = nextTier ?? (currentTier ?? DESIGNER_TIERS[0]);
  const performance = Math.min((totalArts / targetTier.monthlyArts) * 100, 100);
  const remaining = nextTier ? Math.max(0, nextTier.monthlyArts - totalArts) : 0;
  return { currentTier, nextTier, targetTier, bonus, performance, remaining };
}


const SalesBySeller: React.FC = () => {
  const { month: currentMonth, year: currentYear } = getCurrentMonthYearBrasilia();
  const [period, setPeriod] = useState(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
  const [openGoalsDialog, setOpenGoalsDialog] = useState(false);
  const [goalValues, setGoalValues] = useState({
    dailyGoal: '',
    monthlyBonusGoal: '',
  });
  const [bonusTiers, setBonusTiers] = useState<BonusTier[]>([
    { minSales: 0, maxSales: 50000, rate: 0.005, fixedBonus: 0 },
    { minSales: 50000, maxSales: 60000, rate: 0.01, fixedBonus: 0 },
    { minSales: 60000, maxSales: 100000, rate: 0.015, fixedBonus: 250 },
    { minSales: 100000, maxSales: null, rate: 0.02, fixedBonus: 500 },
  ]);
  const [commercialSalesTotal, setCommercialSalesTotal] = useState(0);
  const [commercialOrdersTotal, setCommercialOrdersTotal] = useState(0);
  const [siteSalesTotal, setSiteSalesTotal] = useState(0);
  const [siteOrdersTotal, setSiteOrdersTotal] = useState(0);
  const [evolutionData, setEvolutionData] = useState<Array<{ month: string; comercial: number; site: number }>>([]);
  const [sellerPerformances, setSellerPerformances] = useState<SellerPerformance[]>([]);
  const [designerPerformances, setDesignerPerformances] = useState<DesignerPerformance[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [savingGoals, setSavingGoals] = useState(false);
  const [savingBonus, setSavingBonus] = useState(false);
  const [goalsDialogTab, setGoalsDialogTab] = useState(0);
  const [siteMonthlyGoal, setSiteMonthlyGoal] = useState(25000);
  const [openSiteGoalModal, setOpenSiteGoalModal] = useState(false);
  const [siteGoalInput, setSiteGoalInput] = useState('');
  const [savingSiteGoal, setSavingSiteGoal] = useState(false);

  const selectedPeriod = useMemo(() => {
    const [year, month] = period.split('-').map(Number);
    return { year, month };
  }, [period]);

  const periodOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];
    const baseDate = new Date(currentYear, currentMonth - 1, 1);
    for (let i = 0; i < 12; i += 1) {
      const date = new Date(baseDate);
      date.setMonth(date.getMonth() - i);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const value = `${year}-${String(month).padStart(2, '0')}`;
      const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  }, [currentMonth, currentYear]);


  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { month, year } = selectedPeriod;

      // Chamadas paralelas otimizadas
      const [billingStatsResult, siteStatsResult, designerProductivityResult] = await Promise.allSettled([
        apiService.getBillingStats({ month, year }),
        apiService.getSiteStats(month, year),
        apiService.getDesignerProductivity('mes-atual', month, year),
      ]);

      const billingStats = billingStatsResult.status === 'fulfilled' ? billingStatsResult.value : null;
      const siteStats = siteStatsResult.status === 'fulfilled' ? siteStatsResult.value : null;
      const designerProductivity = designerProductivityResult.status === 'fulfilled' ? designerProductivityResult.value : null;

      // Debug log detalhado
      console.log('📊 Billing Stats:', billingStatsResult.status, billingStats);
      console.log('📊 Site Stats resultado:', siteStatsResult);
      console.log('📊 Site Stats dados:', siteStats);
      if (siteStatsResult.status === 'rejected') {
        console.error('❌ Erro ao buscar site stats:', (siteStatsResult as PromiseRejectedResult).reason);
      }

      // Dados comerciais (vendas dos vendedores)
      const commercialTotal = Number(billingStats?.summary?.totalMaterialValue ?? billingStats?.summary?.totalValue ?? 0);
      const commercialOrders = Number(billingStats?.summary?.totalSalesCount || 0);

      // Dados do site (Irroba - banco local ou API)
      const siteTotal = Number(siteStats?.siteSalesTotal ?? 0);
      const siteOrders = Number(siteStats?.siteOrdersTotal ?? 0);
      
      console.log('💰 Valores calculados:', { 
        commercialTotal, 
        commercialOrders, 
        siteTotal, 
        siteOrders,
        rawSiteStats: siteStats 
      });

      setCommercialSalesTotal(commercialTotal);
      setCommercialOrdersTotal(commercialOrders);
      setSiteSalesTotal(siteTotal);
      setSiteOrdersTotal(siteOrders);

      // Performance individual dos vendedores
      const salesBySeller = billingStats?.salesBySeller || [];
      setSellerPerformances(salesBySeller.map((s: any) => ({
        id: s.id,
        name: s.name,
        monthlyGoal: Number(s.monthlyGoal ?? 0),
        goalIsFallback: Boolean(s.goalIsFallback),
        salesRealized: Number(s.totalMaterial ?? 0),
        numberOfOrders: Number(s.salesCount ?? 0),
        performance: Number(s.performance ?? 0),
        bonus: Number(s.bonus ?? 0),
      })));

      // Performance individual dos designers
      const designersList = (designerProductivity as any)?.designers || [];
      setDesignerPerformances(designersList.map((d: any) => ({
        id: d.designerId,
        name: d.designerName,
        totalArts: Number(d.totalArts ?? 0),
      })));

      // Gráfico de evolução: combinar histórico comercial + site
      const monthNameToNumber: Record<string, number> = {
        Jan: 1, Fev: 2, Mar: 3, Abr: 4, Mai: 5, Jun: 6,
        Jul: 7, Ago: 8, Set: 9, Out: 10, Nov: 11, Dez: 12,
      };

      const siteByMonth = new Map<string, number>();
      (siteStats?.monthlyHistory || []).forEach((item: any) => {
        const key = `${item.year}-${String(monthNameToNumber[item.month] || 0).padStart(2, '0')}`;
        siteByMonth.set(key, Number(item.total || 0));
      });

      const commercialHistory = billingStats?.monthlyHistory || [];
      const evolution = commercialHistory.map((item: any) => {
        const key = `${item.year}-${String(monthNameToNumber[item.month] || 0).padStart(2, '0')}`;
        return {
          month: item.month,
          comercial: Number(item.total || 0),
          site: siteByMonth.get(key) || 0,
        };
      });

      setEvolutionData(evolution);

      // Meta mensal do site
      try {
        const goal = await apiService.getSiteMonthlyGoal();
        setSiteMonthlyGoal(goal);
      } catch {
        // manter valor atual
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas de vendas:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [selectedPeriod]);

  // Performance dos vendedores (dados reais do backend)
  const sellers = sellerPerformances;
  const totalGoal = sellers.reduce((sum, seller) => sum + seller.monthlyGoal, 0);
  const isPastMonth = selectedPeriod.year < currentYear || (selectedPeriod.year === currentYear && selectedPeriod.month < currentMonth);
  const hasFallbackGoals = sellers.some(s => s.goalIsFallback);
  const totalSales = commercialSalesTotal + siteSalesTotal;
  const totalOrders = commercialOrdersTotal + siteOrdersTotal;
  const overallPerformance = totalGoal > 0 ? (totalSales / totalGoal) * 100 : (sellers.length > 0 ? 0 : 0);

  const getPerformanceColor = (performance: number) => {
    if (performance >= 100) return '#10b981';
    if (performance >= 80) return '#f59e0b';
    return '#ef4444';
  };

  const getPerformanceBarColor = (performance: number) => {
    if (performance >= 100) return 'success';
    if (performance >= 80) return 'warning';
    return 'error';
  };

  const handleOpenGoals = async () => {
    const avgMonthlyGoal = sellers.length > 0
      ? sellers.reduce((sum, s) => sum + s.monthlyGoal, 0) / sellers.length
      : 15000;
    setGoalValues({
      dailyGoal: (avgMonthlyGoal / 22).toFixed(2),
      monthlyBonusGoal: avgMonthlyGoal.toFixed(2),
    });
    try {
      const config = await apiService.getSellerBonusConfig();
      if (config?.tiers?.length) setBonusTiers(config.tiers);
    } catch {
      // usar default
    }
    setOpenGoalsDialog(true);
  };

  const handleCloseGoals = () => {
    setOpenGoalsDialog(false);
    setGoalsDialogTab(0);
  };

  const handleSaveGoals = async () => {
    const value = parseFloat(goalValues.monthlyBonusGoal || '0');
    if (isNaN(value) || value < 0) return;
    setSavingGoals(true);
    try {
      await apiService.setSellerTargets(selectedPeriod.month, selectedPeriod.year, value);
      await loadStats();
      handleCloseGoals();
    } catch (err) {
      console.error('Erro ao salvar metas:', err);
    } finally {
      setSavingGoals(false);
    }
  };

  const handleSaveBonusConfig = async () => {
    setSavingBonus(true);
    try {
      await apiService.setSellerBonusConfig({ tiers: bonusTiers });
      await loadStats();
    } catch (err) {
      console.error('Erro ao salvar bonificação:', err);
    } finally {
      setSavingBonus(false);
    }
  };

  const handleOpenSiteGoalModal = () => {
    setSiteGoalInput(siteMonthlyGoal.toFixed(2));
    setOpenSiteGoalModal(true);
  };

  const handleCloseSiteGoalModal = () => {
    setOpenSiteGoalModal(false);
  };

  const handleSaveSiteGoal = async () => {
    const value = parseFloat(siteGoalInput.replace(',', '.')) || 0;
    if (value < 0) return;
    setSavingSiteGoal(true);
    try {
      await apiService.setSiteMonthlyGoal(value);
      setSiteMonthlyGoal(value);
      handleCloseSiteGoalModal();
    } catch (err) {
      console.error('Erro ao salvar meta do site:', err);
    } finally {
      setSavingSiteGoal(false);
    }
  };

  const updateBonusTier = (index: number, field: keyof BonusTier, value: number | null) => {
    setBonusTiers((prev) => {
      const next = [...prev];
      if (field === 'maxSales') next[index] = { ...next[index], maxSales: value };
      else next[index] = { ...next[index], [field]: value as number };
      return next;
    });
  };

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Desempenho de Vendas
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Acompanhe o desempenho da sua equipe de vendas
          </Typography>
        </Box>
        
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periodOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Vendas Total */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#e0e7ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6366f1',
                  }}
                >
                  <TargetIcon />
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                Vendas Total
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                R$ {totalSales.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Vendas Comercial */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#d1fae5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#10b981',
                  }}
                >
                  <AttachMoneyIcon />
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                Vendas Comercial
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>
                R$ {commercialSalesTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Vendas Site */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#e0e7ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6366f1',
                  }}
                >
                  <StoreIcon />
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                Vendas Site
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#6366f1' }}>
                R$ {siteSalesTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Geral */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#fce7f3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ec4899',
                  }}
                >
                  <TrendingUpIcon />
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                Performance Geral
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: getPerformanceColor(overallPerformance) }}>
                {overallPerformance.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total de Pedidos */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#3b82f6',
                  }}
                >
                  <PeopleIcon />
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                Total de Pedidos
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                {totalOrders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráfico de Evolução */}
      <Card sx={{ mb: 4, boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Evolução de Vendas
          </Typography>
          
          {evolutionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={evolutionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="month" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value, name) => [
                    `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                    name === 'Comercial' ? 'Vendas Comercial' : 'Vendas Site'
                  ]}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="comercial" 
                  name="Comercial"
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', r: 6 }}
                  activeDot={{ r: 8 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="site" 
                  name="Site"
                  stroke="#6366f1" 
                  strokeWidth={3}
                  dot={{ fill: '#6366f1', r: 6 }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Gráfico de evolução em desenvolvimento
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                Visualização temporal das vendas
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Performance Individual - Vendedores */}
      <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Performance Individual - Vendedores
            </Typography>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              size="small"
              onClick={handleOpenGoals}
              sx={{ textTransform: 'none' }}
            >
              Gerenciar Metas
            </Button>
          </Box>

          {isPastMonth && hasFallbackGoals && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
              Metas com &quot;(ref.)&quot; usam o valor do mês atual como referência para comparativo, pois não havia meta cadastrada neste mês.
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {sellers.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
                Nenhum vendedor ativo no período ou sem dados de faturamento.
              </Typography>
            ) : (
            sellers.map((seller) => (
              <Paper 
                key={seller.id} 
                sx={{ 
                  p: 3, 
                  backgroundColor: '#f9fafb',
                  boxShadow: 'none',
                  border: '1px solid #e5e7eb',
                }}
              >
                {/* Header do vendedor */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {seller.name}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 700, 
                      color: getPerformanceColor(seller.performance),
                    }}
                  >
                    {seller.performance.toFixed(1)}% da meta
                  </Typography>
                </Box>

                {/* Informações */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                      Meta Diária{seller.goalIsFallback ? ' (ref.)' : ''}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                      R$ {(seller.monthlyGoal / 22).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                      Meta Mensal (Bônus){seller.goalIsFallback ? ' (ref.)' : ''}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#10b981' }}>
                      R$ {seller.monthlyGoal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                      Vendas Realizadas
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      R$ {seller.salesRealized.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                      Bônus Calculado
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#059669' }}>
                      R$ {(seller.bonus ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                      Número de Pedidos
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {seller.numberOfOrders}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Barra de progresso */}
                <Box sx={{ position: 'relative' }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(seller.performance, 100)} 
                    color={getPerformanceBarColor(seller.performance)}
                    sx={{ 
                      height: 10, 
                      borderRadius: 5,
                      backgroundColor: '#e5e7eb',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                      }
                    }}
                  />
                  {seller.performance > 100 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: -2,
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: '#10b981',
                        border: '2px solid white',
                      }}
                    />
                  )}
                </Box>
              </Paper>
            ))
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Performance Individual - Designers */}
      <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Performance Individual - Designers
            </Typography>
          </Box>

          {/* Legenda dos tiers */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {DESIGNER_TIERS.map((tier) => (
              <Box
                key={tier.monthlyArts}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  fontSize: '11px',
                  color: '#64748b',
                  whiteSpace: 'nowrap',
                }}
              >
                {tier.artsPerDay} artes/dia → <strong>R$ {tier.bonus}</strong>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {designerPerformances.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
                Nenhum designer ativo no período ou sem dados de artes.
              </Typography>
            ) : (
              designerPerformances.map((designer) => {
                const { currentTier, nextTier, targetTier, bonus, performance, remaining } = getDesignerTierInfo(designer.totalArts);
                const atMaxTier = currentTier !== null && nextTier === null;

                return (
                  <Paper
                    key={designer.id}
                    sx={{
                      p: 3,
                      backgroundColor: '#f9fafb',
                      boxShadow: 'none',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        {designer.name}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 700, color: getPerformanceColor(performance) }}
                      >
                        {performance.toFixed(1)}% da meta
                      </Typography>
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                          Meta Diária
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                          {targetTier.artsPerDay} artes/dia
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                          Meta Mensal (Gratificação)
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#10b981' }}>
                          {targetTier.monthlyArts.toLocaleString('pt-BR')} artes
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                          Artes Realizadas
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {designer.totalArts.toLocaleString('pt-BR')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                          Gratificação Calculada
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#059669' }}>
                          R$ {bonus.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                          {atMaxTier ? 'Status' : 'Próxima Meta'}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: atMaxTier ? '#10b981' : '#1e293b' }}
                        >
                          {atMaxTier
                            ? 'Meta máxima atingida!'
                            : `Faltam ${remaining.toLocaleString('pt-BR')} artes → R$ ${nextTier!.bonus}`}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box sx={{ position: 'relative' }}>
                      <LinearProgress
                        variant="determinate"
                        value={performance}
                        color={getPerformanceBarColor(performance)}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: '#e5e7eb',
                          '& .MuiLinearProgress-bar': { borderRadius: 5 },
                        }}
                      />
                      {atMaxTier && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: -2,
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            backgroundColor: '#10b981',
                            border: '2px solid white',
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Performance de Loja Virtual */}
      <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <StoreIcon sx={{ color: '#6366f1' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Performance de Loja Virtual
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
              (Vendas diretas no site)
            </Typography>
          </Box>

          {/* Stats da Loja Virtual - dados reais */}
          {(() => {
            const sitePerformance = siteMonthlyGoal > 0 ? (siteSalesTotal / siteMonthlyGoal) * 100 : 0;
            return (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                component="button"
                onClick={handleOpenSiteGoalModal}
                sx={{
                  p: 2,
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  transition: 'all 0.2s',
                  '&:hover': { backgroundColor: '#dcfce7', borderColor: '#86efac' },
                  '&:focus': { outline: 'none' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                    Meta Mensal Site
                  </Typography>
                  <EditIcon sx={{ fontSize: 16, color: '#10b981' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>
                  R$ {siteMonthlyGoal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, fontSize: '12px' }}>
                  Vendas do Site
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                  R$ {siteSalesTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, fontSize: '12px' }}>
                  Performance Site
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getPerformanceColor(sitePerformance) }}>
                  {sitePerformance.toFixed(1)}%
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: '#e0e7ff', border: '1px solid #c7d2fe' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, fontSize: '12px' }}>
                  Pedidos do Site
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#6366f1' }}>
                  {siteOrdersTotal}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
            );
          })()}

          {/* Métricas do Site */}
          <Paper 
            sx={{ 
              p: 3, 
              backgroundColor: '#f9fafb',
              boxShadow: 'none',
              border: '1px solid #e5e7eb',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
              Indicadores de Vendas do Site
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: '8px' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Ticket Médio
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {siteOrdersTotal > 0
                      ? `R$ ${(siteSalesTotal / siteOrdersTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : 'R$ 0,00'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Barra de progresso geral - dados reais */}
            {(() => {
              const sitePerformance = siteMonthlyGoal > 0 ? (siteSalesTotal / siteMonthlyGoal) * 100 : 0;
              const siteRemaining = Math.max(0, siteMonthlyGoal - siteSalesTotal);
              return (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Progresso da Meta do Site
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: getPerformanceColor(sitePerformance) }}>
                  {sitePerformance.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={Math.min(sitePerformance, 100)} 
                color={sitePerformance >= 100 ? 'success' : sitePerformance >= 80 ? 'warning' : 'error'}
                sx={{ 
                  height: 12, 
                  borderRadius: 6,
                  backgroundColor: '#e5e7eb',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 6,
                  }
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                {siteRemaining > 0
                  ? `Faltam R$ ${siteRemaining.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} para atingir a meta mensal do site`
                  : 'Meta mensal do site atingida ou superada.'}
              </Typography>
            </Box>
              );
            })()}
          </Paper>
        </CardContent>
      </Card>

      {/* Modal de Configuração de Metas */}
      <Dialog 
        open={openGoalsDialog} 
        onClose={handleCloseGoals}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Gerenciar Metas
            </Typography>
          </Box>
          <IconButton onClick={handleCloseGoals} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Tabs value={goalsDialogTab} onChange={(_, v) => setGoalsDialogTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Metas" />
          <Tab label="Bonificação" />
        </Tabs>
        <DialogContent dividers>
          {goalsDialogTab === 0 && (
          <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Configure a meta diária de vendas e a meta mensal para bonificação.
          </Typography>
          <Typography variant="body2" sx={{ color: '#f59e0b', fontWeight: 600, mb: 3 }}>
            ⚠️ As metas serão aplicadas para todos os {sellers.length} vendedores.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Meta Diária */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#3b82f6' }}>
                📅 Meta Diária
              </Typography>
              <TextField
                fullWidth
                label="Meta Diária de Vendas"
                value={goalValues.dailyGoal}
                onChange={(e) => setGoalValues({ ...goalValues, dailyGoal: e.target.value })}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>R$</Typography>,
                }}
                helperText="Valor que o vendedor deve tentar atingir todo dia"
              />
            </Box>

            {/* Meta Mensal de Bonificação */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#10b981' }}>
                🎯 Meta Mensal de Bonificação
              </Typography>
              <TextField
                fullWidth
                label="Meta para Bonificação"
                value={goalValues.monthlyBonusGoal}
                onChange={(e) => setGoalValues({ ...goalValues, monthlyBonusGoal: e.target.value })}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>R$</Typography>,
                }}
                helperText="Meta mensal que garante bonificação ao vendedor"
              />
            </Box>
          </Box>

          {/* Preview */}
          <Paper sx={{ mt: 3, p: 2, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              📊 Visualização das Metas
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Meta Diária:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                    R$ {parseFloat(goalValues.dailyGoal || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                  Em 22 dias úteis: R$ {(parseFloat(goalValues.dailyGoal || '0') * 22).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              
              <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Meta Mensal (Bonificação):
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#10b981' }}>
                    R$ {parseFloat(goalValues.monthlyBonusGoal || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Informação Adicional */}
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
            <Typography variant="caption" sx={{ color: '#1e40af', display: 'block', mb: 0.5 }}>
              💡 <strong>Como funciona:</strong>
            </Typography>
            <Typography variant="caption" sx={{ color: '#1e40af', display: 'block' }}>
              • A meta diária orienta todos os vendedores no dia a dia
            </Typography>
            <Typography variant="caption" sx={{ color: '#1e40af', display: 'block' }}>
              • Ao atingir a meta mensal, o vendedor recebe bonificação
            </Typography>
            <Typography variant="caption" sx={{ color: '#1e40af', display: 'block' }}>
              • As mesmas metas serão aplicadas para toda a equipe
            </Typography>
          </Box>
          </>
          )}
          {goalsDialogTab === 1 && (
          <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Configure as faixas de bonificação sobre as vendas realizadas pelo vendedor comercial.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {bonusTiers.map((tier, idx) => (
              <Paper key={idx} sx={{ p: 2, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Faixa {idx + 1}: {tier.maxSales != null
                    ? `R$ ${(tier.minSales / 1000).toFixed(0)}k até R$ ${(tier.maxSales / 1000).toFixed(0)}k`
                    : `Acima de R$ ${(tier.minSales / 1000).toFixed(0)}k`}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Vendas mín (R$)"
                      type="number"
                      value={tier.minSales}
                      onChange={(e) => updateBonusTier(idx, 'minSales', parseFloat(e.target.value) || 0)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Vendas máx (R$) ou vazio"
                      type="number"
                      value={tier.maxSales ?? ''}
                      onChange={(e) => updateBonusTier(idx, 'maxSales', e.target.value === '' ? null : parseFloat(e.target.value) || 0)}
                      placeholder="Acima"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Taxa (0.01 = 1%)"
                      type="number"
                      inputProps={{ step: 0.001, min: 0, max: 1 }}
                      value={tier.rate}
                      onChange={(e) => updateBonusTier(idx, 'rate', parseFloat(e.target.value) || 0)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Bônus fixo (R$)"
                      type="number"
                      value={tier.fixedBonus}
                      onChange={(e) => updateBonusTier(idx, 'fixedBonus', parseFloat(e.target.value) || 0)}
                    />
                  </Grid>
                </Grid>
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                  Fórmula: Vendas × {((tier.rate || 0) * 100).toFixed(2)}% {tier.fixedBonus ? `+ R$ ${tier.fixedBonus}` : ''}
                </Typography>
              </Paper>
            ))}
          </Box>
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
            <Typography variant="caption" sx={{ color: '#166534', display: 'block' }}>
              💡 <strong>Exemplo padrão:</strong> &lt;50k = 0,5% | ≤60k = 1% | ≤100k = 1,5%+R$250 | &gt;100k = 2%+R$500
            </Typography>
          </Box>
          </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseGoals} color="inherit">
            Cancelar
          </Button>
          {goalsDialogTab === 0 ? (
          <Button 
            onClick={handleSaveGoals} 
            variant="contained" 
            color="primary"
            disabled={savingGoals}
            startIcon={<SettingsIcon />}
          >
            {savingGoals ? 'Salvando...' : 'Salvar Metas'}
          </Button>
          ) : (
          <Button 
            onClick={handleSaveBonusConfig} 
            variant="contained" 
            sx={{ background: '#059669', '&:hover': { background: '#047857' } }}
            disabled={savingBonus}
            startIcon={<SettingsIcon />}
          >
            {savingBonus ? 'Salvando...' : 'Salvar Bonificação'}
          </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal Meta Mensal do Site */}
      <Dialog open={openSiteGoalModal} onClose={handleCloseSiteGoalModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StoreIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Meta Mensal do Site
            </Typography>
          </Box>
          <IconButton onClick={handleCloseSiteGoalModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Meta (R$)"
            value={siteGoalInput}
            onChange={(e) => setSiteGoalInput(e.target.value)}
            type="number"
            inputProps={{ step: 100, min: 0 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseSiteGoalModal} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleSaveSiteGoal}
            variant="contained"
            sx={{ background: '#10b981', '&:hover': { background: '#059669' } }}
            disabled={savingSiteGoal}
          >
            {savingSiteGoal ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesBySeller;
